// client/src/admin/AdminDashboard.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import AdminLayout from "./components/AdminLayout";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import StayCurrentPortraitIcon from "@mui/icons-material/StayCurrentPortrait";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import CancelIcon from "@mui/icons-material/Cancel";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import axios from "axios";
import "./AdminDashboard.css";

const AdminDashboard = () => {
  const navigate = useNavigate();

  const [products, setProducts] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const authHeaders = useMemo(
    () => ({
      headers: {
        Authorization: "Bearer " + localStorage.getItem("token"),
      },
    }),
    []
  );

  // DASHBOARD STATS
  const getDashboardStats = async () => {
    try {
      setLoading(true);
      const res = await axios.get("/api/admin/dashboard-stats", authHeaders);

      if (res.data?.success) {
        setStats(res.data.data);
      } else {
        message.error(res.data?.message || "Failed to load dashboard stats");
        setStats(null);
      }
    } catch (error) {
      console.log(error);
      message.error("Failed to load dashboard stats");
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  // PRODUCTS
  const getAllProducts = async () => {
    try {
      const res = await axios.get("/api/product/get-all-products", authHeaders);

      if (res.data?.success) {
        setProducts((res.data.data || []).reverse());
      } else {
        message.error(res.data?.message || "Failed to load products");
      }
    } catch (error) {
      console.log(error);
      message.error("Failed to load products");
    }
  };

  // helpers
  const formatK = (num) => {
    const n = Number(num || 0);
    if (!Number.isFinite(n)) return 0;
    if (n >= 1000) return n % 1000 === 0 ? `${n / 1000}k` : `${(n / 1000).toFixed(1)}k`;
    return n;
  };

  const totalOrders = stats?.totalOrders || 0;

  const successOrders = stats?.successOrders || 0;
  const successAmount = stats?.successAmount || 0;

  const failedOrders = stats?.failedOrders || 0;
  const failedAmount = stats?.failedAmount || 0;

  const pendingOrders = stats?.pendingOrders || 0;
  const pendingAmount = stats?.pendingAmount || 0;

  useEffect(() => {
    getDashboardStats();
    getAllProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const MiniLoader = () => (
    <div className="spinner-border spinner-border-sm" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  );

  return (
    <AdminLayout>
      <div className="page-title">
        <h3 className="m-0">Dashboard</h3>
      </div>
      <hr />

      <div className="admin-dashboard-container p-0">
        {/* Total Orders */}
        <div className="dash-card" onClick={() => navigate("/admin-orders")}>
          <div className="count">
            <h1 className="m-0">{loading ? <MiniLoader /> : <b>{formatK(totalOrders)}</b>}</h1>
            <span>Total Orders</span>
          </div>
          <PointOfSaleIcon className="icon" />
        </div>

        {/* Success Orders */}
        <div className="dash-card" onClick={() => navigate("/admin-orders")}>
          <div className="count">
            <h1 className="m-0">{loading ? <MiniLoader /> : <b>{formatK(successOrders)}</b>}</h1>
            <span>Success Orders</span>
          </div>
          <CheckCircleIcon className="icon" />
        </div>

        {/* Failed Orders */}
        <div className="dash-card" onClick={() => navigate("/admin-orders")}>
          <div className="count">
            <h1 className="m-0">{loading ? <MiniLoader /> : <b>{formatK(failedOrders)}</b>}</h1>
            <span>Failed Orders</span>
          </div>
          <CancelIcon className="icon" />
        </div>

        {/* Pending Orders */}
        <div className="dash-card" onClick={() => navigate("/admin-orders")}>
          <div className="count">
            <h1 className="m-0">{loading ? <MiniLoader /> : <b>{formatK(pendingOrders)}</b>}</h1>
            <span>Pending Orders</span>
          </div>
          <HourglassEmptyIcon className="icon" />
        </div>

        {/* Total Products */}
        <div className="dash-card" onClick={() => navigate("/admin-products")}>
          <div className="count">
            <h1 className="m-0">{loading ? <MiniLoader /> : <b>{products?.length || 0}</b>}</h1>
            <span>Total Products</span>
          </div>
          <StayCurrentPortraitIcon className="icon" />
        </div>

        {/* Success Amount (Total Sales) */}
        <div className="dash-card" onClick={() => navigate("/admin-payments")}>
          <div className="count">
            <h1 className="m-0">{loading ? <MiniLoader /> : <b>{formatK(successAmount)}</b>}</h1>
            <span>Success Amount</span>
          </div>
          <MonetizationOnIcon className="icon" />
        </div>

        {/* Failed Amount */}
        <div className="dash-card" onClick={() => navigate("/admin-orders")}>
          <div className="count">
            <h1 className="m-0">{loading ? <MiniLoader /> : <b>{formatK(failedAmount)}</b>}</h1>
            <span>Failed Amount</span>
          </div>
          <CancelIcon className="icon" />
        </div>

        {/* Pending Amount */}
        <div className="dash-card" onClick={() => navigate("/admin-orders")}>
          <div className="count">
            <h1 className="m-0">{loading ? <MiniLoader /> : <b>{formatK(pendingAmount)}</b>}</h1>
            <span>Pending Amount</span>
          </div>
          <HourglassEmptyIcon className="icon" />
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
